import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import type { Rol } from '@/lib/permisos'
import { supabase } from './supabase'

export interface Perfil {
  id: string
  nombre: string
  rol: Rol
  cliente_id: string | null
  muelle_id: string | null
  operador_id: string | null
  activo: boolean
}

export interface EstadoSesion {
  cargando: boolean
  sesion: Session | null
  /** Perfil leído de la base (fuente de verdad, R-19), no del token. */
  perfil: Perfil | null
  salir: () => Promise<void>
}

const Contexto = createContext<EstadoSesion | null>(null)

async function leerPerfil(id: string): Promise<Perfil | null> {
  const { data, error } = await supabase()
    .from('perfiles')
    .select('id, nombre, rol, cliente_id, muelle_id, operador_id, activo')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return (data as Perfil | null) ?? null
}

export function SesionProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Omit<EstadoSesion, 'salir'>>({
    cargando: true,
    sesion: null,
    perfil: null,
  })

  useEffect(() => {
    let vigente = true
    const cargar = async (sesion: Session | null) => {
      if (!sesion) {
        if (vigente) setEstado({ cargando: false, sesion: null, perfil: null })
        return
      }
      try {
        const perfil = await leerPerfil(sesion.user.id)
        if (vigente) setEstado({ cargando: false, sesion, perfil })
      } catch {
        if (vigente) setEstado({ cargando: false, sesion, perfil: null })
      }
    }
    let cliente: ReturnType<typeof supabase>
    try {
      cliente = supabase()
    } catch (e) {
      // Sin variables de entorno (p. ej. solo para ver /dev/sistema): la app sigue sin sesión.
      console.error(e)
      setEstado({ cargando: false, sesion: null, perfil: null })
      return
    }
    const { data } = cliente.auth.onAuthStateChange((evento, sesion) => {
      // Las renovaciones de token no cambian el perfil: no se vuelve a consultar.
      if (evento === 'TOKEN_REFRESHED') return
      // Se difiere para no llamar a la base dentro del callback de auth.
      setTimeout(() => void cargar(sesion), 0)
    })
    return () => {
      vigente = false
      data.subscription.unsubscribe()
    }
  }, [])

  const salir = async () => {
    await supabase().auth.signOut()
  }

  return <Contexto.Provider value={{ ...estado, salir }}>{children}</Contexto.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSesion(): EstadoSesion {
  const s = useContext(Contexto)
  if (!s) throw new Error('useSesion fuera de SesionProvider')
  return s
}

/** Para pruebas: un proveedor con un estado fijo. */
export function SesionFija({
  valor,
  children,
}: {
  valor: Partial<EstadoSesion>
  children: ReactNode
}) {
  return (
    <Contexto.Provider
      value={{
        cargando: false,
        sesion: null,
        perfil: null,
        salir: async () => undefined,
        ...valor,
      }}
    >
      {children}
    </Contexto.Provider>
  )
}
